import React, { useState, useEffect } from 'react'
import { retrieveConfig } from '../../config/config'
import { mapErrorMessage, getGenericErrorMessage } from '../../utils/error-message-mapper'
import { sendPostMessage } from '../../utils/post-message'

export default function PaymentFailure({ visible, chargeId, message, errorMessage }) {
    const [config, setConfig] = useState(null)
    const cdnHost = window.env.REACT_APP_CDN_HOST

    useEffect(() => {
        async function loadConfig() {
            try {
                const result = await retrieveConfig()
                setConfig(result)
            } catch (error) {
                console.error('Error loading config:', error)
            }
        }
        loadConfig()
    }, [])

    // Get the primary color from the config
    const primaryColor = config?.colorTheme?.primaryColor ?? '#d32f2f'

    // Handle both string and object message formats, prioritize errorMessage prop for backward compatibility
    const rawErrorMessage = errorMessage || (typeof message === 'object' ? message.message : message)
    const isRetryable = typeof message === 'object' ? message.isRetryable : true

    // Map the error message to determine if it's a customer error and get appropriate generic message
    const errorInfo = mapErrorMessage(rawErrorMessage)
    const genericMessage = getGenericErrorMessage(errorInfo.isCustomerError)

    // Send post message with retryable information when component mounts
    useEffect(() => {
        if (visible && chargeId && rawErrorMessage) {
            sendPostMessage('PAYMENT_FAILED', {
                charge_id: chargeId,
                error: rawErrorMessage,
                retryable: isRetryable,
                timestamp: new Date().toISOString(),
            })
        }
    }, [visible, chargeId, rawErrorMessage, isRetryable])

    const handleTryAgain = () => {
        // Get the payment session ID from session storage
        const paymentSessionId = sessionStorage.getItem('paymentSessionId')

        if (paymentSessionId) {
            // Redirect back to the SDK page with the original session ID
            const baseUrl = window.location.origin
            const sdkUrl = `${baseUrl}/sdk/${paymentSessionId}`
            window.location.href = sdkUrl
        } else {
            // Last resort: redirect to home page
            const baseUrl = window.location.origin
            window.location.href = baseUrl
        }
    }

    if (!visible) {
        return null
    }

    const transFailedIcon = cdnHost + '/app/transaction_failed.svg'

    const styles = {
        container: {
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '600px',
            margin: '23px 22px 18px 16px',
            overflow: 'hidden',
        },
        header: {
            background: '#E6E6E6',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
        },
        icon: {
            width: '80px',
            height: '80px',
            marginBottom: '16px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '8px',
            textAlign: 'center',
            color: '#cf0909',
        },
        systemError: {
            fontWeight: 'bold',
            fontSize: '18px',
            color: '#cf0909',
            textAlign: 'center',
        },
        message: {
            fontSize: '14px',
            color: '#6B7280',
            textAlign: 'center',
            maxWidth: '400px',
            lineHeight: '1.5',
            marginTop: '16px',
        },
        details: {
            padding: '24px',
            background: 'white',
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px',
        },
        button: {
            backgroundColor: primaryColor,
            color: 'white',
            padding: '10px 20px',
            borderRadius: '4px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        transactionId: {
            fontSize: '12px',
            color: '#9CA3AF',
            textAlign: 'left',
            marginTop: '20px',
            fontWeight: '400',
        },
    }

    return (
        <div style={styles.container} className="failure-box">
            <div style={styles.header}>
                <img src={transFailedIcon} alt="Failed" style={styles.icon} />
                <div style={styles.title}>Payment Failed</div>
            </div>
            <div style={styles.details}>
                <div style={styles.systemError}>{rawErrorMessage}</div>
                <div style={styles.message}>{genericMessage}</div>
                {isRetryable && (
                    <div style={styles.buttonContainer}>
                        <button style={styles.button} onClick={handleTryAgain}>
                            Try Again
                        </button>
                    </div>
                )}
                {chargeId && <div style={styles.transactionId}>Transaction ID: {chargeId}</div>}
            </div>
        </div>
    )
}
